/**
 * Domain-aware listing approval: operator catalogue review vs automatic
 * price/inventory sync. Pure helpers — no provider I/O.
 */

import {
  DOMAIN_LABELS,
  isAutomaticSyncDomain,
  isOperatorSyncDomain,
  operatorDirtyDomains,
} from "./phase1-sync-contract";
import type { ApprovalState } from "./channels";

export const OPERATOR_HISTORY_FIELDS: Record<string, string> = {
  name: "content",
  description: "content",
  short_description: "content",
  seo_title: "seo",
  meta_description: "seo",
  seo_keywords: "seo",
  weight_kg: "shipping",
  length_cm: "shipping",
  width_cm: "shipping",
  height_cm: "shipping",
  shipping_profile: "shipping",
  sku: "variants",
  status: "variants",
  option_values: "variants",
  attribute: "attributes",
  media_added: "media",
  media_removed: "media",
  category: "category",
};

export const AUTOMATIC_HISTORY_FIELDS: Record<string, string> = {
  selling_price: "pricing",
  cost_price: "pricing",
};

export type HistoryKind = "automatic" | "operator" | "internal";

export function classifyListingHistoryField(field: string): { domain: string; kind: HistoryKind } {
  if (OPERATOR_HISTORY_FIELDS[field]) {
    return { domain: OPERATOR_HISTORY_FIELDS[field], kind: "operator" };
  }
  if (AUTOMATIC_HISTORY_FIELDS[field]) {
    return { domain: AUTOMATIC_HISTORY_FIELDS[field], kind: "automatic" };
  }
  return { domain: field, kind: "internal" };
}

export function listingApprovalState(listing: {
  approved_version: number | null;
  intended_version?: number;
  operator_revision?: number | null;
  dirty_domains?: string[] | null;
}): ApprovalState {
  if (listing.approved_version == null || listing.approved_version === 0) return "none";
  const revision = listing.operator_revision;
  if (revision != null) {
    return revision > listing.approved_version ? "stale" : "approved";
  }
  if (operatorDirtyDomains(listing.dirty_domains).length > 0) return "stale";
  return "approved";
}

export function shouldOfferListingApproval(state: ApprovalState): boolean {
  return state === "none" || state === "stale";
}

/**
 * Short operator label for a listing whose operator-domain revision has not been
 * approved. A newly created channel draft is NOT automatically approval-ready:
 * `approved_version` starts empty, so it is genuinely pending.
 *
 * Returns null when there is nothing to say (already approved).
 */
export function listingApprovalNotice(state: ApprovalState): string | null {
  if (state === "none") return "Approval pending";
  if (state === "stale") return "Approval out of date";
  return null;
}

export function approvalPendingSummary(dirty: string[] | null | undefined): string | null {
  const ops = operatorDirtyDomains(dirty).map((d) => DOMAIN_LABELS[d] ?? d);
  if (!ops.length) return null;
  return `Pending: ${ops.join(" · ")}`;
}

export interface ApprovalReviewRow {
  domain: string;
  domainLabel: string;
  kind: HistoryKind;
  from: string;
  to: string;
  requiresApproval: boolean;
  note: string;
}

export interface ApprovalReview {
  rows: ApprovalReviewRow[];
  operatorRows: ApprovalReviewRow[];
  automaticRows: ApprovalReviewRow[];
  requiresApproval: boolean;
}

function displayValue(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  return s === "" ? "(inherited / empty)" : s;
}

export function buildListingApprovalReview(opts: {
  dirtyDomains?: string[] | null;
  history: Array<{ field: string; old_value?: string | null; new_value?: string | null; created_at?: string }>;
}): ApprovalReview {
  const rows: ApprovalReviewRow[] = [];
  const seenDirty = new Set<string>();
  for (const h of opts.history) {
    const { domain, kind } = classifyListingHistoryField(h.field);
    if (kind === "internal") continue;
    const requiresApproval = kind === "operator";
    rows.push({
      domain,
      domainLabel: DOMAIN_LABELS[domain] ?? domain,
      kind,
      from: displayValue(h.old_value),
      to: displayValue(h.new_value),
      requiresApproval,
      note: requiresApproval ? "Approval required" : "Automatic sync — no approval required",
    });
    if (requiresApproval) seenDirty.add(domain);
  }
  for (const d of operatorDirtyDomains(opts.dirtyDomains)) {
    if (seenDirty.has(d)) continue;
    rows.push({
      domain: d,
      domainLabel: DOMAIN_LABELS[d] ?? d,
      kind: "operator",
      from: "Approved snapshot",
      to: "Changed since approval",
      requiresApproval: true,
      note: "Approval required",
    });
  }
  const operatorRows = rows.filter((r) => r.requiresApproval);
  const automaticRows = rows.filter((r) => !r.requiresApproval);
  return {
    rows,
    operatorRows,
    automaticRows,
    requiresApproval: operatorRows.length > 0,
  };
}

export function reconcileOperatorRevision(opts: {
  approvedVersion: number;
  operatorDirty: boolean;
  hasOperatorHistorySinceApproval: boolean;
}): number {
  const approved = Math.max(opts.approvedVersion, 0);
  const pending = opts.operatorDirty || opts.hasOperatorHistorySinceApproval;
  if (approved <= 0) return pending ? 1 : 1;
  return pending ? approved + 1 : approved;
}

export function automaticSyncDoesNotStaleApproval(domain: string): boolean {
  return isAutomaticSyncDomain(domain);
}

export function operatorDomainStalesApproval(domain: string): boolean {
  return isOperatorSyncDomain(domain);
}

export function lastFullPublishLabel(): string {
  return "Last full publish";
}

export function lastSyncLabel(): string {
  return "Last sync";
}
